/**
 * i18next configuration for GIERMEK.online frontend.
 *
 * Locale priority:
 *   (1) Game session locale — set imperatively via i18next.changeLanguage() in handleStateUpdate
 *   (2) Logged-in user locale from authStore — set imperatively post-initAuth in App.tsx
 *   (3) navigator.language — detected by custom detector at init time
 *   (4) fallback 'pl'
 *
 * Custom detector handles priorities (3)/(4) synchronously at init.
 * Priorities (1)/(2) are applied imperatively after async data is available.
 */

import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import { detectSupportedLocale } from '@/types/auth'
import plTranslations from './locales/pl.json'
import enTranslations from './locales/en.json'

/**
 * Custom language detector that checks user locale from authStore
 * (priority 2) then navigator.language (priority 3) then falls back to 'pl'.
 *
 * Uses lazy import for authStore to prevent circular dependency.
 */
const grorinatorLocaleDetector = {
    type: 'languageDetector' as const,
    async: false,
    detect(): string {
        // Priority (2): logged-in user locale from authStore (sync getState)
        // Lazy require avoids circular import if authStore ever imports from i18n
        try {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAuthStore } = require('@/stores/authStore')
            const user = useAuthStore.getState().user
            if (user?.locale) {
                return user.locale
            }
        } catch {
            // authStore not available yet — continue
        }

        // Priority (3): navigator.language
        if (typeof navigator !== 'undefined' && navigator.language) {
            return detectSupportedLocale(navigator.language)
        }

        // Priority (4): fallback
        return 'pl'
    },
    init: () => {},
    cacheUserLanguage: () => {},
}

i18next
    .use(grorinatorLocaleDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: 'pl',
        interpolation: {
            escapeValue: false, // React already escapes
        },
        resources: {
            pl: { translation: plTranslations },
            en: { translation: enTranslations },
        },
        react: {
            useSuspense: false, // Prevents Suspense crash if no Suspense boundary
        },
    })

export default i18next
